// src/app/settings/accruals/pages/leave-policy-history/leave-policy-history.component.ts

import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Subject, forkJoin } from 'rxjs';
import {
  debounceTime,
  distinctUntilChanged,
  finalize,
  takeUntil,
} from 'rxjs/operators';

import { AccrualsService } from '../accruals.service';
import {
  EmployeeUser,
  LeavePolicy,
  LeavePolicyStartDateRow,
  LeaveType,
} from '../models/accruals.model';
import { AccrualsNavComponent } from '../../../shared/accruals-nav/accruals-nav.component';

interface PolicyHistoryFiltersVm {
  searchTerm: string;
  employeeId: number | null;
  leavePolicyId: number | null;
  leaveType: LeaveType | null;
  startDate: string | null;
  endDate: string | null;
}

@Component({
  selector: 'app-leave-policy-history',
  standalone: true,
  imports: [CommonModule, FormsModule, AccrualsNavComponent],
  templateUrl: './leave-policy-history.component.html',
  styleUrls: ['./leave-policy-history.component.scss'],
})
export class LeavePolicyHistoryComponent implements OnInit, OnDestroy {
  isLoading = false;
  errorMessage: string | null = null;

  allHistoryRows: LeavePolicyStartDateRow[] = [];
  filteredHistoryRows: LeavePolicyStartDateRow[] = [];

  allUsers: EmployeeUser[] = [];
  allPolicies: LeavePolicy[] = [];

  leaveTypes: LeaveType[] = ['Vacation', 'Sick'];

  currentPage = 1;
  totalPages = 1;
  pageSize = 25;
  pageSizes = [25, 50, 100, 250];

  filters: PolicyHistoryFiltersVm = {
    searchTerm: '',
    employeeId: null,
    leavePolicyId: null,
    leaveType: null,
    startDate: null,
    endDate: null,
  };

  searchInputValue = '';
  private readonly searchInput$ = new Subject<string>();
  private readonly destroy$ = new Subject<void>();

  constructor(
    private readonly accrualsService: AccrualsService,
    private readonly cd: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    this.bindSearch();
    this.loadInitialData();
  }

  ngOnDestroy(): void {
    this.searchInput$.complete();
    this.destroy$.next();
    this.destroy$.complete();
  }

  get hasActiveFilters(): boolean {
    return !!(
      this.filters.searchTerm.trim() ||
      this.filters.employeeId !== null ||
      this.filters.leavePolicyId !== null ||
      this.filters.leaveType !== null ||
      this.filters.startDate ||
      this.filters.endDate
    );
  }

  get pagedHistoryRows(): LeavePolicyStartDateRow[] {
    const startIndex = (this.currentPage - 1) * this.pageSize;
    return this.filteredHistoryRows.slice(startIndex, startIndex + this.pageSize);
  }

  loadInitialData(): void {
    this.isLoading = true;
    this.errorMessage = null;
    this.cd.detectChanges();

    forkJoin({
      historyRows: this.accrualsService.getAllLeavePolicyStartDates(),
      users: this.accrualsService.getAllUsers(),
      policies: this.accrualsService.getLeavePolicies(),
    })
      .pipe(
        finalize(() => {
          this.isLoading = false;
          this.cd.detectChanges();
        }),
        takeUntil(this.destroy$),
      )
      .subscribe({
        next: ({ historyRows, users, policies }) => {
          this.allHistoryRows = [...(historyRows ?? [])].sort((a, b) => {
            const deletedDateCompare = String(b.deletedAt ?? '').localeCompare(String(a.deletedAt ?? ''));
            if (deletedDateCompare !== 0) return deletedDateCompare;

            const createdDateCompare = String(b.createdAt ?? '').localeCompare(String(a.createdAt ?? ''));
            if (createdDateCompare !== 0) return createdDateCompare;

            return String(b.startDate).localeCompare(String(a.startDate));
          });

          this.filteredHistoryRows = [...this.allHistoryRows];
          this.currentPage = 1;
          this.updatePagination();

          this.allUsers = [...(users ?? [])].sort((a, b) =>
            `${a.lastName} ${a.firstName}`.localeCompare(`${b.lastName} ${b.firstName}`),
          );

          this.allPolicies = [...(policies ?? [])].sort((a, b) => a.name.localeCompare(b.name));

          this.cd.detectChanges();
        },
        error: () => {
          this.errorMessage = 'Failed to load leave policy history.';
          this.allHistoryRows = [];
          this.filteredHistoryRows = [];
          this.currentPage = 1;
          this.totalPages = 1;
          this.cd.detectChanges();
        },
      });
  }

  private bindSearch(): void {
    this.searchInput$
      .pipe(debounceTime(1000), distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe((value) => {
        this.filters.searchTerm = value;
        this.currentPage = 1;
        this.applyFilters();
      });
  }

  onSearchTermChange(value: string | null): void {
    const normalized = String(value ?? '').trimStart();
    this.searchInputValue = normalized;
    this.searchInput$.next(normalized);
  }

  onEmployeeChange(value: number | string | null): void {
    this.filters.employeeId = this.toNullableNumber(value);
    this.currentPage = 1;
    this.applyFilters();
  }

  onPolicyChange(value: number | string | null): void {
    this.filters.leavePolicyId = this.toNullableNumber(value);
    this.currentPage = 1;
    this.applyFilters();
  }

  onLeaveTypeChange(value: LeaveType | '' | null): void {
    this.filters.leaveType = value ? value : null;
    this.currentPage = 1;
    this.applyFilters();
  }

  onStartDateChange(value: string | null): void {
    this.filters.startDate = value || null;
    this.currentPage = 1;
    this.applyFilters();
  }

  onEndDateChange(value: string | null): void {
    this.filters.endDate = value || null;
    this.currentPage = 1;
    this.applyFilters();
  }

  clearFilters(): void {
    this.filters = {
      searchTerm: '',
      employeeId: null,
      leavePolicyId: null,
      leaveType: null,
      startDate: null,
      endDate: null,
    };

    this.searchInputValue = '';
    this.currentPage = 1;
    this.applyFilters();
  }

  applyFilters(): void {
    const search = this.filters.searchTerm.trim().toLowerCase();
    const startDate = this.filters.startDate ? new Date(this.filters.startDate) : null;
    const endDate = this.filters.endDate ? new Date(this.filters.endDate) : null;

    if (endDate) {
      endDate.setHours(23, 59, 59, 999);
    }

    this.filteredHistoryRows = this.allHistoryRows.filter((row) => {
      const employeeFullName = `${row.employee.firstName} ${row.employee.lastName}`.toLowerCase();
      const policyName = row.leavePolicy.name.toLowerCase();
      const rowDate = new Date(row.startDate);

      const normalizedLeaveType = this.normalizeLeaveType(row.leavePolicy.leaveType);
      const normalizedAccrualType = this.normalizeAccrualFrequency(row.leavePolicy.accrualFrequency);
      const deletedByName = row.deletedBy
        ? `${row.deletedBy.firstName} ${row.deletedBy.lastName}`.toLowerCase()
        : '';
      const deletedStateText = row.isDeleted ? 'deleted' : 'active';

      const matchesSearch =
        !search ||
        employeeFullName.includes(search) ||
        row.employee.employeeNumber.toLowerCase().includes(search) ||
        policyName.includes(search) ||
        normalizedLeaveType.toLowerCase().includes(search) ||
        normalizedAccrualType.toLowerCase().includes(search) ||
        deletedStateText.includes(search) ||
        deletedByName.includes(search);

      const matchesEmployee =
        this.filters.employeeId === null || row.employee.id === this.filters.employeeId;

      const matchesPolicy =
        this.filters.leavePolicyId === null || row.leavePolicy.id === this.filters.leavePolicyId;

      const matchesLeaveType =
        this.filters.leaveType === null || normalizedLeaveType === this.filters.leaveType;

      const matchesStartDate = !startDate || rowDate >= startDate;
      const matchesEndDate = !endDate || rowDate <= endDate;

      return (
        matchesSearch &&
        matchesEmployee &&
        matchesPolicy &&
        matchesLeaveType &&
        matchesStartDate &&
        matchesEndDate
      );
    });

    this.updatePagination();
    this.cd.detectChanges();
  }

  changePage(page: number): void {
    if (page < 1 || page > this.totalPages) {
      return;
    }

    this.currentPage = page;
    this.cd.detectChanges();
  }

  changePageSize(size: number): void {
    this.pageSize = Number(size);
    this.currentPage = 1;
    this.updatePagination();
    this.cd.detectChanges();
  }

  getLeaveTypeClass(value: number | string): string {
    return this.normalizeLeaveType(value) === 'Sick' ? 'sick' : 'vacation';
  }

  getNormalizedLeaveType(value: number | string): LeaveType {
    return this.normalizeLeaveType(value);
  }

  getNormalizedAccrualFrequency(value: number | string): string {
    return this.normalizeAccrualFrequency(value);
  }

  trackByHistoryId(_: number, item: LeavePolicyStartDateRow): number {
    return item.id;
  }

  private updatePagination(): void {
    this.totalPages = Math.max(1, Math.ceil(this.filteredHistoryRows.length / this.pageSize));

    if (this.currentPage > this.totalPages) {
      this.currentPage = this.totalPages;
    }
  }

  private normalizeLeaveType(value: number | string): LeaveType {
    if (typeof value === 'string') {
      return value.toLowerCase() === 'sick' ? 'Sick' : 'Vacation';
    }

    return value === 2 ? 'Sick' : 'Vacation';
  }

  private normalizeAccrualFrequency(value: number | string): string {
    if (typeof value === 'string') {
      return value;
    }

    switch (value) {
      case 1:
        return 'Daily';
      case 2:
        return 'Weekly';
      case 3:
        return 'PerPayPeriod';
      case 4:
        return 'Monthly';
      case 5:
        return 'YearlyFrontLoad';
      default:
        return String(value);
    }
  }

  private toNullableNumber(value: number | string | null | undefined): number | null {
    if (value === null || value === undefined || value === '') {
      return null;
    }

    const parsed = Number(value);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
  }
}